import { isTauri, registerUploadPath } from '../../utils/tauri';
import { ensureTrailingEmptyFormDataRow } from '../../utils/multipart';
import { makeKvRowId, ensureTrailingEmptyKvRow } from '../../utils/kvRows';

/** Params, headers, cookies and form-data rows: edit, rename and remove. */
export function useRequestKeyValueEditors({
  url,
  auth,
  body,
  name,
  emitDataChange,
  showToast,
  params,
  headers,
  cookies,
  method,
  setHeaders,
  setParams,
  setCookies,
  bodyType,
  formDataFields,
  setFormDataFields,
  formDataFileInputRef,
  pendingFormDataFileIndexRef,
  notifyChange,
  notifyParamsAndUrl,
}) {
  // Simplified update functions without complex dependencies
  const updateHeaders = (index, field, value) => {
    const newHeaders = headers.map((h, i) =>
      i === index ? { ...h, [field]: value } : h
    );
    setHeaders(newHeaders);
    emitDataChange({ name, method, url, headers: newHeaders, params, cookies, body, bodyType, auth });
  };

  const updateParams = (index, field, value) => {
    // Build new params array from current state (not the functional-update form)
    // so that notifyParamsAndUrl is called OUTSIDE the setState updater — calling
    // setUrl/onDataChange inside an updater is a React anti-pattern (double-invoked
    // in Strict Mode) and causes interleaved state that scrambles the params caret.
    const newParams = params.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    );
    setParams(newParams);
    notifyParamsAndUrl(newParams);
  };

  /** Defer trailing-row append so SyntaxHighlightInput finishes one render without row-count jump (fixes caret jumble). */
  const handleParamKeyChange = (index, value) => {
    const prev = params;
    const prevKey = prev[index]?.key ?? '';
    const newParams = prev.map((p, i) =>
      i === index ? { ...p, key: value } : p
    );

    const shouldAppendTrailing =
      index === prev.length - 1 && prevKey === '' && value.length > 0;

    setParams(newParams);
    notifyParamsAndUrl(newParams);

    if (shouldAppendTrailing) {
      // Double rAF: append after the browser finishes the current key event + layout (Space needs this).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setParams((p) => {
            const last = p[p.length - 1];
            if (!last || last.key === '') return p;
            // Trailing empty row: URL doesn't change (empty key is filtered),
            // so no notifyParamsAndUrl call needed here.
            return [...p, { key: '', value: '', enabled: true, _id: makeKvRowId('p') }];
          });
        });
      });
    }
  };

  const handleHeaderKeyChange = (index, value) => {
    const prevKey = headers[index]?.key ?? '';
    const newHeaders = headers.map((h, i) =>
      i === index ? { ...h, key: value } : h
    );

    const shouldAppendTrailing =
      index === headers.length - 1 && prevKey === '' && value.length > 0;

    setHeaders(newHeaders);
    emitDataChange({ name, method, url, headers: newHeaders, params, cookies, body, bodyType, auth });

    if (shouldAppendTrailing) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setHeaders((p) => {
            const last = p[p.length - 1];
            if (!last || last.key === '') return p;
            return [...p, { key: '', value: '', enabled: true, _id: makeKvRowId('h') }];
          });
        });
      });
    }
  };

  const handleCookieKeyChange = (index, value) => {
    setCookies((prev) => {
      const rows = [...prev];
      const prevKey = rows[index].key;
      rows[index] = { ...rows[index], key: value };

      const shouldAppendTrailing =
        index === prev.length - 1 && prevKey === '' && value.length > 0;

      emitDataChange({ name, method, url, headers, params, cookies: rows, body, bodyType, auth });

      if (shouldAppendTrailing) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setCookies((p) => {
              const last = p[p.length - 1];
              if (!last || last.key === '') return p;
              const next = [...p, { key: '', value: '', enabled: true, _id: makeKvRowId('c') }];
              emitDataChange({ name, method, url, headers, params, cookies: next, body, bodyType, auth });
              return next;
            });
          });
        });
      }

      return rows;
    });
  };

  const removeHeader = (index) => {
    let newHeaders = headers.filter((_, i) => i !== index);
    if (newHeaders.length === 0) {
      newHeaders = [{ key: '', value: '', enabled: true, _id: `h-0-${Date.now()}` }];
    } else {
      newHeaders = ensureTrailingEmptyKvRow(newHeaders, 'h');
    }
    setHeaders(newHeaders);
    emitDataChange({ name, method, url, headers: newHeaders, params, cookies, body, bodyType, auth });
  };

  const removeParam = (index) => {
    let newParams = params.filter((_, i) => i !== index);
    if (newParams.length === 0) {
      newParams = [{ key: '', value: '', enabled: true, _id: `p-0-${Date.now()}` }];
    } else {
      newParams = ensureTrailingEmptyKvRow(newParams, 'p');
    }
    setParams(newParams);
    notifyParamsAndUrl(newParams);
  };

  const updateCookies = (index, field, value) => {
    setCookies(prev => {
      const newCookies = [...prev];
      newCookies[index] = { ...newCookies[index], [field]: value };
      // Notify parent of the change
      emitDataChange({ name, method, url, headers, params, cookies: newCookies, body, bodyType, auth });
      return newCookies;
    });
  };

  const removeCookie = (index) => {
    setCookies((prev) => {
      let newCookies = prev.filter((_, i) => i !== index);
      if (newCookies.length === 0) {
        newCookies = [{ key: '', value: '', enabled: true, _id: `c-0-${Date.now()}` }];
      } else {
        newCookies = ensureTrailingEmptyKvRow(newCookies, 'c');
      }
      emitDataChange({ name, method, url, headers, params, cookies: newCookies, body, bodyType, formDataFields, auth });
      return newCookies;
    });
  };

  const updateFormDataFields = (index, field, value) => {
    setFormDataFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'type' && value === 'text') {
        next[index].filePath = null;
        next[index].file = undefined;
        next[index].fileName = undefined;
      }
      if (field === 'type' && value === 'file') {
        next[index].value = '';
        next[index].filePath = null;
        next[index].file = undefined;
        next[index].fileName = undefined;
      }
      const withTrailing = ensureTrailingEmptyFormDataRow(next);
      notifyChange({ formDataFields: withTrailing });
      return withTrailing;
    });
  };

  const removeFormDataField = (index) => {
    setFormDataFields((prev) => {
      let next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        next = [{ key: '', value: '', type: 'text', enabled: true, filePath: null, _id: `fd-0-${Date.now()}` }];
      } else {
        next = ensureTrailingEmptyFormDataRow(next);
      }
      notifyChange({ formDataFields: next });
      return next;
    });
  };

  const pickFormDataFile = async (index) => {
    if (isTauri()) {
      try {
        const { open } = await import('@tauri-apps/plugin-dialog');
        const selected = await open({ multiple: false });
        if (selected && typeof selected === 'string') {
          // COL-01: this pick is the ONLY thing that makes a path uploadable.
          // Register before storing it, and use the canonical path Rust
          // returned, so what the row holds is exactly what the send-time gate
          // will accept. If registration fails the row keeps no path at all
          // rather than one that would be refused on Send.
          const registered = await registerUploadPath(selected);
          const path = typeof registered === 'string' && registered ? registered : selected;
          const fileName = path.split(/[/\\]/).pop() || path;
          updateFormDataFields(index, 'filePath', path);
          setFormDataFields((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], fileName, type: 'file' };
            notifyChange({ formDataFields: next });
            return next;
          });
        }
      } catch (err) {
        showToast(err.message || 'Could not open file picker', 'error');
      }
      return;
    }
    pendingFormDataFileIndexRef.current = index;
    formDataFileInputRef.current?.click();
  };

  const handleFormDataFileInput = (e) => {
    const file = e.target.files?.[0];
    const index = pendingFormDataFileIndexRef.current;
    e.target.value = '';
    pendingFormDataFileIndexRef.current = null;
    if (file == null || index == null) return;
    setFormDataFields((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        type: 'file',
        file,
        fileName: file.name,
        filePath: null,
        value: file.name,
      };
      const withTrailing = ensureTrailingEmptyFormDataRow(next);
      notifyChange({ formDataFields: withTrailing });
      return withTrailing;
    });
  };

  return {
    updateHeaders,
    updateParams,
    handleParamKeyChange,
    handleHeaderKeyChange,
    handleCookieKeyChange,
    removeHeader,
    removeParam,
    updateCookies,
    removeCookie,
    updateFormDataFields,
    removeFormDataField,
    pickFormDataFile,
    handleFormDataFileInput,
  };
}
